import environment from "@environment";
import { RequestHelper, UrlBuilder, UrlUtils } from "@lib-shared/lib";
import context from "core/tv-guide.context";
import { Channel, ChannelsWithProgrammes, Genre, Programme } from "core/types";

export class BaseService {
    protected baseUrl = "";

    abortController: AbortController | null = null;

    constructor(path = "", abortController: AbortController | null = null) {
        this.abortController = abortController;
        this.setBaseUrl(path)
    }

    protected setBaseUrl(path: string) {
        this.baseUrl = UrlUtils.join(environment.apiUrlBase, path);
    }

    protected getRequestHelper(path: string | undefined) {
        const helper = new RequestHelper(this.baseUrl, this.abortController?.signal)
        return helper.withPath(path)
    }

    protected createPromiseRequest(path: string | undefined): RequestHelper {
        const helper = this.getRequestHelper(path);

        return helper.usingPromise();
    }

    protected async get<T>(urlBuilder: UrlBuilder) {
        return this.createPromiseRequest(urlBuilder.toString()).get<T>();
    }

    // protected async post<T>(urlBuilder: TvGuideUrlBuilder) {
    //     return this.createPromiseRequest(urlBuilder.toString()).get<T>();
    // }
}

const urlBuilder = (path: string) => {
    return new TvGuideUrlBuilder(path);
}

class TvGuideUrlBuilder extends UrlBuilder {
    private _appendGuideParams = () => {
        const { guideCountryCode, guideUnit, culture } = context;

        if (guideCountryCode) {
            this.appendParam("country", guideCountryCode);
        }

        if (guideUnit) {
            this.appendParam("unit", guideUnit);
        }

        if (culture) {
            this.appendParam("culture", culture);
        }
    }

    override toString(): string {
        this._appendGuideParams()
        return super.toString();
    }
}

class TvGuideService extends BaseService {
    constructor() {
        super("/umbraco/api/tv-guide");
    }

    async getChannels($package = "", genre = "", subGenre = "") {
        const url = urlBuilder("channels");

        if ($package) {
            url.appendParam("package", $package);
        }

        if (genre) {
            url.appendParam("genre", genre);
        }

        if (subGenre) {
            url.appendParam("subGenre", subGenre);
        }

        return this.get<Channel[]>(url);
    }

    async getChannel(channelTag: string) {
        const url = urlBuilder("channel").appendParam("channelTag", channelTag);

        return this.get<Channel>(url);
    }

    async getGenres() {
        const url = urlBuilder("genres");

        return this.get<Genre[]>(url);
    }

    async getPackages() {
        const url = urlBuilder("packages");

        return this.get<string[]>(url);
    }

    async getProgrammeById(id: string) {
        const url = urlBuilder("programme").appendParam("id", id)

        return this.get<Programme>(url);
    }

    async getProgrammeByEventId(id: string) {
        const url = urlBuilder("programme").appendParam("eventId", id);

        return this.get<Programme>(url);
    }

    async getProgrammes(date: string, $package = "", genre = "", subGenre = "") {
        const url = urlBuilder("programmes").appendParam("date", date);

        if ($package) {
            url.appendParam("package", $package);
        }

        if (genre) {
            url.appendParam("genre", genre);
        }

        if (subGenre) {
            url.appendParam("subGenre", subGenre);
        }

        return this.get<ChannelsWithProgrammes>(url);
    }
}

export default TvGuideService;